// Sign-in accounts and their role assignments.
//
// An assignment is a (role, project) pair; project null means every project,
// which is how an administrator or a planner working across sites is set up.
// The same user can hold several — Project Manager on Harita and Viewer on
// Ekam is a normal arrangement.

import { useState } from 'react';
import {
    Check,
    KeyRound,
    Loader2,
    Plus,
    ShieldOff,
    Trash2,
    UserPlus,
    X,
} from 'lucide-react';
import { accessApi, type AccessUser, type Role } from '../../lib/api';

type Project = { id: string; name: string };
type Assignment = { role_id: number; project_id: string | null };

type Props = {
    users: AccessUser[];
    roles: Role[];
    projects: Project[];
    onChanged: () => Promise<void>;
};

const ALL_PROJECTS = '__all__';

export default function UsersTab({ users, roles, projects, onChanged }: Props) {
    const [creating, setCreating] = useState(false);
    const [editingRolesFor, setEditingRolesFor] = useState<AccessUser | null>(null);
    const [resettingFor, setResettingFor] = useState<AccessUser | null>(null);
    const [busyId, setBusyId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    const withBusy = async (id: number, fn: () => Promise<void>) => {
        setBusyId(id);
        setError(null);
        try {
            await fn();
            await onChanged();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Action failed');
        } finally {
            setBusyId(null);
        }
    };

    const loginUsers = users.filter((u) => u.has_login);
    const rosterOnly = users.filter((u) => !u.has_login);

    return (
        <div className="space-y-6">
            {error && (
                <p
                    role="alert"
                    className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-md px-3 py-2"
                >
                    {error}
                </p>
            )}

            <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                    Accounts with sign-in access
                    <span className="ml-2 text-sm font-normal text-gray-500">{loginUsers.length}</span>
                </h2>
                <button
                    onClick={() => setCreating(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
                >
                    <UserPlus size={15} /> Add user
                </button>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-slate-700/40 text-left">
                        <tr className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            <th className="px-4 py-2.5 font-medium">User</th>
                            <th className="px-4 py-2.5 font-medium">Access</th>
                            <th className="px-4 py-2.5 font-medium">Last sign-in</th>
                            <th className="px-4 py-2.5 font-medium text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loginUsers.map((u) => (
                            <tr
                                key={u.id}
                                className="border-t border-gray-100 dark:border-slate-700/60 align-top"
                            >
                                <td className="px-4 py-3">
                                    <div className="font-medium text-gray-900 dark:text-gray-100">
                                        {u.name}
                                        {!u.is_active && (
                                            <span className="ml-2 text-xs font-normal px-1.5 py-0.5 rounded bg-gray-200 dark:bg-slate-600 text-gray-600 dark:text-gray-300">
                                                disabled
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {u.username}
                                        {u.job_title ? ` · ${u.job_title}` : ''}
                                    </div>
                                </td>
                                <td className="px-4 py-3">
                                    {u.assignments.length === 0 ? (
                                        <span className="text-xs text-amber-600 dark:text-amber-400">
                                            No role — cannot do anything yet
                                        </span>
                                    ) : (
                                        <div className="flex flex-wrap gap-1">
                                            {u.assignments.map((a) => (
                                                <span
                                                    key={a.assignment_id}
                                                    className="inline-flex items-center gap-1 text-xs rounded px-1.5 py-0.5 bg-indigo-50 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300"
                                                >
                                                    {a.role_name}
                                                    <span className="text-indigo-400 dark:text-indigo-500">
                                                        {a.project_name ?? 'all projects'}
                                                    </span>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                    {u.last_login_at
                                        ? new Date(u.last_login_at).toLocaleString()
                                        : 'never'}
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center justify-end gap-1">
                                        {busyId === u.id && (
                                            <Loader2 size={14} className="animate-spin text-gray-400" />
                                        )}
                                        <button
                                            onClick={() => setEditingRolesFor(u)}
                                            title="Edit roles and projects"
                                            className="px-2 py-1 text-xs rounded text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                                        >
                                            Roles
                                        </button>
                                        <button
                                            onClick={() => setResettingFor(u)}
                                            title="Reset password"
                                            className="p-1.5 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700"
                                        >
                                            <KeyRound size={14} />
                                        </button>
                                        <button
                                            onClick={() =>
                                                withBusy(u.id, () =>
                                                    accessApi.updateUser(u.id, { is_active: !u.is_active }),
                                                )
                                            }
                                            title={u.is_active ? 'Disable account' : 'Enable account'}
                                            className="p-1.5 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700"
                                        >
                                            {u.is_active ? <ShieldOff size={14} /> : <Check size={14} />}
                                        </button>
                                        <button
                                            onClick={() => {
                                                if (
                                                    window.confirm(
                                                        `Revoke sign-in access for ${u.name}? They stay on the team roster but can no longer log in.`,
                                                    )
                                                ) {
                                                    withBusy(u.id, () => accessApi.revokeLogin(u.id));
                                                }
                                            }}
                                            title="Revoke login"
                                            className="p-1.5 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {loginUsers.length === 0 && (
                            <tr>
                                <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500">
                                    No accounts can sign in yet.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Team members without a login: the pool to grant access to. */}
            {rosterOnly.length > 0 && (
                <details className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
                    <summary className="px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                        Team members without sign-in access
                        <span className="ml-2 text-gray-500 font-normal">{rosterOnly.length}</span>
                    </summary>
                    <div className="px-4 pb-4 flex flex-wrap gap-1.5">
                        {rosterOnly.map((u) => (
                            <span
                                key={u.id}
                                className="text-xs rounded px-2 py-1 bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300"
                            >
                                {u.name}
                                {u.job_title ? ` · ${u.job_title}` : ''}
                            </span>
                        ))}
                    </div>
                    <p className="px-4 pb-4 text-xs text-gray-500 dark:text-gray-400">
                        These are roster entries from the ERP sync and project team lists. Use “Add
                        user” to create a login, matching the name.
                    </p>
                </details>
            )}

            {creating && (
                <CreateUserDialog
                    roles={roles}
                    projects={projects}
                    onClose={() => setCreating(false)}
                    onCreated={async () => {
                        setCreating(false);
                        await onChanged();
                    }}
                />
            )}

            {editingRolesFor && (
                <EditRolesDialog
                    user={editingRolesFor}
                    roles={roles}
                    projects={projects}
                    onClose={() => setEditingRolesFor(null)}
                    onSaved={async () => {
                        setEditingRolesFor(null);
                        await onChanged();
                    }}
                />
            )}

            {resettingFor && (
                <ResetPasswordDialog
                    user={resettingFor}
                    onClose={() => setResettingFor(null)}
                    onDone={() => setResettingFor(null)}
                />
            )}
        </div>
    );
}

// ---------------------------------------------------------------- dialogs

function Dialog({
    title,
    onClose,
    children,
}: {
    title: string;
    onClose: () => void;
    children: React.ReactNode;
}) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-lg bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-gray-200 dark:border-slate-700 max-h-[85vh] flex flex-col">
                <header className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200 dark:border-slate-700">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">{title}</h3>
                    <button
                        onClick={onClose}
                        className="p-1 rounded text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700"
                        aria-label="Close"
                    >
                        <X size={18} />
                    </button>
                </header>
                <div className="flex-1 min-h-0 overflow-auto p-5">{children}</div>
            </div>
        </div>
    );
}

/** Editable list of (role, project) pairs, shared by create and edit. */
function AssignmentEditor({
    assignments,
    roles,
    projects,
    onChange,
}: {
    assignments: Assignment[];
    roles: Role[];
    projects: Project[];
    onChange: (next: Assignment[]) => void;
}) {
    const update = (i: number, patch: Partial<Assignment>) =>
        onChange(assignments.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));

    return (
        <div className="space-y-2">
            {assignments.map((a, i) => (
                <div key={i} className="flex items-center gap-2">
                    <select
                        value={a.role_id || ''}
                        onChange={(e) => update(i, { role_id: Number(e.target.value) })}
                        className="flex-1 min-w-0 rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm dark:text-white"
                    >
                        <option value="" disabled>
                            Choose a role…
                        </option>
                        {roles.map((r) => (
                            <option key={r.id} value={r.id}>
                                {r.name}
                            </option>
                        ))}
                    </select>

                    <select
                        value={a.project_id ?? ALL_PROJECTS}
                        onChange={(e) =>
                            update(i, {
                                project_id: e.target.value === ALL_PROJECTS ? null : e.target.value,
                            })
                        }
                        className="flex-1 min-w-0 rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm dark:text-white"
                    >
                        <option value={ALL_PROJECTS}>All projects</option>
                        {projects.map((p) => (
                            <option key={p.id} value={p.id}>
                                {p.name}
                            </option>
                        ))}
                    </select>

                    <button
                        onClick={() => onChange(assignments.filter((_, idx) => idx !== i))}
                        className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10"
                        aria-label="Remove assignment"
                    >
                        <X size={15} />
                    </button>
                </div>
            ))}

            <button
                onClick={() => onChange([...assignments, { role_id: 0, project_id: null }])}
                className="inline-flex items-center gap-1.5 text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
            >
                <Plus size={14} /> Add role
            </button>

            {assignments.length === 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                    With no roles the user can sign in but will not be able to see or do anything.
                </p>
            )}
        </div>
    );
}

function CreateUserDialog({
    roles,
    projects,
    onClose,
    onCreated,
}: {
    roles: Role[];
    projects: Project[];
    onClose: () => void;
    onCreated: () => Promise<void>;
}) {
    const [form, setForm] = useState({ name: '', username: '', password: '', job_title: '', phone: '' });
    const [assignments, setAssignments] = useState<Assignment[]>([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const submit = async () => {
        setBusy(true);
        setError(null);
        try {
            await accessApi.createUser({
                ...form,
                assignments: assignments.filter((a) => a.role_id),
            });
            await onCreated();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to create user');
            setBusy(false);
        }
    };

    const field = (key: keyof typeof form, label: string, type = 'text', required = false) => (
        <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            <input
                type={type}
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                autoComplete={type === 'password' ? 'new-password' : 'off'}
                className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm dark:text-white"
            />
        </div>
    );

    const valid = form.name && form.username && form.password.length >= 8;

    return (
        <Dialog title="Add user" onClose={onClose}>
            <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                    {field('name', 'Full name', 'text', true)}
                    {field('username', 'Username', 'text', true)}
                    {field('password', 'Password', 'password', true)}
                    {field('job_title', 'Job title')}
                    {field('phone', 'Phone')}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2">
                    Password must be at least 8 characters.
                </p>

                <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Roles and projects
                    </h4>
                    <AssignmentEditor
                        assignments={assignments}
                        roles={roles}
                        projects={projects}
                        onChange={setAssignments}
                    />
                </div>

                {error && (
                    <p role="alert" className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/10 rounded-md px-3 py-2">
                        {error}
                    </p>
                )}

                <div className="flex justify-end gap-2 pt-2">
                    <button onClick={onClose} className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
                        Cancel
                    </button>
                    <button
                        onClick={submit}
                        disabled={!valid || busy}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40"
                    >
                        {busy && <Loader2 size={14} className="animate-spin" />}
                        Create user
                    </button>
                </div>
            </div>
        </Dialog>
    );
}

function EditRolesDialog({
    user,
    roles,
    projects,
    onClose,
    onSaved,
}: {
    user: AccessUser;
    roles: Role[];
    projects: Project[];
    onClose: () => void;
    onSaved: () => Promise<void>;
}) {
    const [assignments, setAssignments] = useState<Assignment[]>(
        user.assignments.map((a) => ({ role_id: a.role_id, project_id: a.project_id })),
    );
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const submit = async () => {
        setBusy(true);
        setError(null);
        try {
            await accessApi.setUserRoles(user.id, assignments.filter((a) => a.role_id));
            await onSaved();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save');
            setBusy(false);
        }
    };

    return (
        <Dialog title={`Roles — ${user.name}`} onClose={onClose}>
            <div className="space-y-4">
                <AssignmentEditor
                    assignments={assignments}
                    roles={roles}
                    projects={projects}
                    onChange={setAssignments}
                />

                {error && (
                    <p role="alert" className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/10 rounded-md px-3 py-2">
                        {error}
                    </p>
                )}

                <div className="flex justify-end gap-2 pt-2">
                    <button onClick={onClose} className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
                        Cancel
                    </button>
                    <button
                        onClick={submit}
                        disabled={busy}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40"
                    >
                        {busy && <Loader2 size={14} className="animate-spin" />}
                        Save roles
                    </button>
                </div>
            </div>
        </Dialog>
    );
}

function ResetPasswordDialog({
    user,
    onClose,
    onDone,
}: {
    user: AccessUser;
    onClose: () => void;
    onDone: () => void;
}) {
    const [password, setPassword] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [done, setDone] = useState(false);

    const submit = async () => {
        setBusy(true);
        setError(null);
        try {
            await accessApi.resetPassword(user.id, password);
            setDone(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to reset password');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog title={`Reset password — ${user.name}`} onClose={onClose}>
            {done ? (
                <div className="space-y-4">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                        Password updated. Pass it to {user.name} over a channel you trust — it is not
                        shown again after you close this.
                    </p>
                    <code className="block text-sm bg-gray-100 dark:bg-slate-900 rounded px-3 py-2 dark:text-gray-200">
                        {password}
                    </code>
                    <div className="flex justify-end">
                        <button
                            onClick={onDone}
                            className="px-3 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
                        >
                            Done
                        </button>
                    </div>
                </div>
            ) : (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            New password
                        </label>
                        <input
                            type="text"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="new-password"
                            className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm dark:text-white"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            At least 8 characters. Shown in clear text so you can copy it before
                            sending it on.
                        </p>
                    </div>

                    {error && (
                        <p role="alert" className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/10 rounded-md px-3 py-2">
                            {error}
                        </p>
                    )}

                    <div className="flex justify-end gap-2">
                        <button onClick={onClose} className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
                            Cancel
                        </button>
                        <button
                            onClick={submit}
                            disabled={password.length < 8 || busy}
                            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40"
                        >
                            {busy && <Loader2 size={14} className="animate-spin" />}
                            Reset password
                        </button>
                    </div>
                </div>
            )}
        </Dialog>
    );
}
